// MetricTrendChart — small SVG line chart of pass-rate over time for a single
// rubric metric. Renders ~240×80px. No deps beyond React.
//
// Input is the sparse `by_date` series from `/api/agents/{vici_user}/metric-trends`
// filtered down to a single metric's per-day pass-rate (0..1). Component re-projects
// to 0..100% on the Y axis, plots a polyline, draws first/last date ticks on X,
// and shows the current pass-rate plus a 7-day-vs-prior-7-day trend arrow above
// the chart.

interface MetricTrendChartProps {
  // Display label for the metric (e.g. "Talk ratio").
  label: string
  // Per-day series, oldest -> newest. Each entry is { date: 'YYYY-MM-DD', rate: 0..1 }.
  series: Array<{ date: string; rate: number }>
  width?: number
  height?: number
}

const WIDTH = 240
const HEIGHT = 80
const PAD_X = 8
const PAD_TOP = 6
// Reserve room at the bottom for the date tick labels.
const PAD_BOTTOM = 14

// Color thresholds keyed off the *current* (latest) pass rate.
//   green  >70%
//   amber  40-70%
//   red    <40%
function pickStroke(latestPct: number): string {
  if (latestPct > 70) return '#34d399' // emerald-400
  if (latestPct >= 40) return '#fbbf24' // amber-400
  return '#f87171' // red-400
}

// Trend arrow comparing avg of the trailing 7 days vs the preceding 7 days.
// "Flat" tolerance is +/- 2 percentage points so daily noise doesn't flip arrows.
function pickTrend(percents: number[]): { arrow: '↑' | '↓' | '→'; color: string } {
  if (percents.length < 2) return { arrow: '→', color: 'text-slate-500' }
  const last7 = percents.slice(-7)
  const prev7 = percents.slice(-14, -7)
  if (prev7.length === 0) return { arrow: '→', color: 'text-slate-500' }
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length
  const delta = avg(last7) - avg(prev7)
  if (delta > 2) return { arrow: '↑', color: 'text-emerald-400' }
  if (delta < -2) return { arrow: '↓', color: 'text-red-400' }
  return { arrow: '→', color: 'text-slate-500' }
}

// Render the date as a short MM/DD label for the X-axis ticks.
function shortDate(iso: string): string {
  // ISO `YYYY-MM-DD` — slice to avoid Date() timezone shifts.
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  return `${parts[1]}/${parts[2]}`
}

export function MetricTrendChart({
  label,
  series,
  width = WIDTH,
  height = HEIGHT,
}: MetricTrendChartProps) {
  if (!series || series.length === 0) {
    return (
      <div className="rounded border border-slate-800 bg-slate-950/40 p-2.5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium text-slate-300">{label}</span>
          <span className="text-[10px] text-slate-600">no data yet</span>
        </div>
        <div
          className="mt-1 flex items-center justify-center text-[10px] text-slate-600"
          style={{ width, height }}
        >
          no data yet
        </div>
      </div>
    )
  }

  // Project rates to whole percents for both math and display.
  const percents = series.map((d) => Math.round(d.rate * 100))
  const latestPct = percents[percents.length - 1]
  const stroke = pickStroke(latestPct)
  const trend = pickTrend(percents)

  // Plot geometry. Y is 0..100 fixed (not series min/max) so charts compare
  // directly across metrics.
  const innerW = Math.max(1, width - PAD_X * 2)
  const innerH = Math.max(1, height - PAD_TOP - PAD_BOTTOM)
  const n = percents.length

  const xFor = (i: number) =>
    n <= 1 ? PAD_X + innerW / 2 : PAD_X + (i / (n - 1)) * innerW
  const yFor = (pct: number) => {
    const clamped = Math.min(100, Math.max(0, pct))
    return PAD_TOP + innerH - (clamped / 100) * innerH
  }

  // Build the polyline path. Single-point series renders a dot below.
  const pathD =
    n > 1
      ? percents
          .map((pct, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(pct).toFixed(2)}`)
          .join(' ')
      : ''

  // Horizontal gridlines at 0 / 50 / 100 — just a faint visual reference.
  const gridYs = [0, 50, 100]

  const firstDate = series[0].date
  const lastDate = series[series.length - 1].date

  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-300">{label}</span>
        <span className="flex items-baseline gap-1.5">
          <span className="font-mono text-xs text-slate-200">{latestPct}%</span>
          <span
            role="img"
            className={`text-sm leading-none ${trend.color}`}
            aria-label={`trend ${trend.arrow}`}
          >
            {trend.arrow}
          </span>
        </span>
      </div>
      <svg
        className="mt-1"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${label} pass rate trend`}
      >
        {/* Faint gridlines */}
        {gridYs.map((g) => (
          <line
            key={g}
            x1={PAD_X}
            x2={width - PAD_X}
            y1={yFor(g)}
            y2={yFor(g)}
            stroke="#1e293b"
            strokeWidth={0.5}
          />
        ))}

        {/* Line or single-point dot */}
        {n > 1 ? (
          <path
            d={pathD}
            fill="none"
            stroke={stroke}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <circle cx={xFor(0)} cy={yFor(percents[0])} r={2} fill={stroke} />
        )}

        {/* X-axis tick labels — first and last only */}
        <text
          x={PAD_X}
          y={height - 2}
          fontSize={9}
          fill="#64748b"
          textAnchor="start"
          fontFamily="ui-monospace, SFMono-Regular, monospace"
        >
          {shortDate(firstDate)}
        </text>
        {firstDate !== lastDate && (
          <text
            x={width - PAD_X}
            y={height - 2}
            fontSize={9}
            fill="#64748b"
            textAnchor="end"
            fontFamily="ui-monospace, SFMono-Regular, monospace"
          >
            {shortDate(lastDate)}
          </text>
        )}
      </svg>
    </div>
  )
}
